import { Declaration } from 'store/declarations/declaration';
import { SubmissionErrors } from 'store/declarations/ireland/ireland-notification';
import { CdsExportNotificationError } from 'store/declarations/uk/cds-declaration';
import extractNotificationsData from 'views/declarations/common/declaration-errors-drawer/notificationUtils';
import { getDeclarationPayload } from 'views/declarations/utils/declaration-utils';
import getErrorFieldContext from 'views/declarations/common/declaration-errors-drawer/declaration-errors/getErrorFieldContext';
import { rejectedStatuses } from 'views/declarations/common/declaration-errors-drawer/shouldShowNotificationErrors';
import resolveErrorField from 'views/declarations/common/declaration-errors-drawer/declaration-errors/resolveErrorField';
import transformCdsErrorField from 'views/declarations/common/declaration-errors-drawer/declaration-errors/cds/transformCdsErrorField';

export type ItemErrorCounts = { [itemId: string]: number };

// An item path through a repeated parent (`consignment.houseConsignment.x.consignmentItem`) names
// the items of every parent at once, so an error position does not point at one array.
const PLACEHOLDER_SEGMENT = '.x.';

// A CDS error names one field, in CDS's own notation - translated to the form's path, counting from zero.
const getCdsFieldsPerError = (errors: CdsExportNotificationError[]) =>
    errors.map((error) => {
        const path = transformCdsErrorField(error.field);
        return path ? [path] : [];
    });

// A Revenue error can name several fields, counting from one.
const getRevenueFieldsPerError = (errors: SubmissionErrors[]) => errors.map((error) => error.fields ?? []);

/**
 * How many errors of the latest customs rejection name each item, by item id. The errors give the
 * position of an item in the declaration that was submitted; the products table is sorted and paged,
 * so the position is turned into the id of the item at it.
 *
 * Items deleted since the submission move the ones after them up, so the positions are read against
 * `submittedDeclaration` - the copy the history kept of the submission - when there is one. Without
 * it the declaration's current items are used, which is only right while they are unchanged.
 *
 * The errors drawer resolves its fields with the same `resolveErrorField`, so the counts on the table
 * and the items on the error cards always agree.
 */
const getRejectedItemErrorCounts = (
    declaration: Declaration | undefined,
    itemPath: string | undefined,
    submittedDeclaration?: Declaration
): ItemErrorCounts => {
    if (!declaration?.status || !rejectedStatuses.includes(declaration.status)) return {};
    if (!itemPath || itemPath.includes(PLACEHOLDER_SEGMENT)) return {};

    const { declarationNotificationErrors } = extractNotificationsData(getDeclarationPayload(declaration));
    if (!Array.isArray(declarationNotificationErrors)) return {};

    const isRevenueImport = Boolean(declaration.irelandImportDeclaration || declaration.irelandH7ImportDeclaration);

    let fieldsPerError: string[][];
    let firstNumber: 0 | 1;
    if (declaration.cdsDeclaration) {
        fieldsPerError = getCdsFieldsPerError(declarationNotificationErrors as CdsExportNotificationError[]);
        firstNumber = 0;
    } else if (isRevenueImport) {
        fieldsPerError = getRevenueFieldsPerError(declarationNotificationErrors as SubmissionErrors[]);
        firstNumber = 1;
    } else {
        // Other types' errors name no item path read here - and their items may not even be loaded.
        return {};
    }

    const fieldContext = getErrorFieldContext(declaration, itemPath, submittedDeclaration);
    if (!fieldContext) return {};

    const context = { ...fieldContext, firstNumber };

    const errorCounts: ItemErrorCounts = {};
    fieldsPerError.forEach((fields) => {
        const itemIds = fields.map((field) => resolveErrorField(field, context).item?.itemId);

        // An error naming several fields of one item is still one error of that item.
        const itemIdsOfError = new Set(itemIds.filter((id): id is string => !!id));
        itemIdsOfError.forEach((itemId) => (errorCounts[itemId] = (errorCounts[itemId] ?? 0) + 1));
    });

    return errorCounts;
};

export default getRejectedItemErrorCounts;
